// # Honors tab component for profile page
import React, { useState, useEffect } from 'react';
import { MdStars } from 'react-icons/md';
import { collection, query, where, getDocs, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { generateCertificatePDF } from '../../../roles/admin/admin_tools/user_manage/usr_mng_utils/user_cert';
import { useAuthStore } from '../../../auth/state';
import './prfl_honors.css';

const ProfileHonors = ({ viewUsername, isAdmin, isDarkMode }) => {
  // # Get current user from auth store
  const { user } = useAuthStore();
  
  // # State to store the profile and issuer data
  const [displayProfile, setDisplayProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [issuerNames, setIssuerNames] = useState({});

  // # Effect to fetch issuer names when certificates or badges change
  useEffect(() => {
    const fetchIssuerNames = async () => {
      if (!displayProfile?.certificates && !displayProfile?.my_badges) return;
      
      const uniqueIssuerIds = [...new Set([
        ...(displayProfile.certificates || []).map(cert => cert.issuedBy?.uid),
        ...(displayProfile.my_badges || []).map(badge => badge.assignedBy?.uid)
      ].filter(uid => uid && !issuerNames[uid]))];

      if (uniqueIssuerIds.length === 0) return;

      try {
        const usersCollection = collection(db, 'users');
        const promises = uniqueIssuerIds.map(async (uid) => {
          const userDoc = await getDocs(query(usersCollection, where('uid', '==', uid)));
          if (!userDoc.empty) {
            const userData = userDoc.docs[0].data();
            return [uid, `${userData.firstName} ${userData.lastName}`.trim() || userData.email || 'Unknown User'];
          }
          return [uid, 'Unknown User'];
        });

        const results = await Promise.all(promises);
        setIssuerNames(prev => ({
          ...prev,
          ...Object.fromEntries(results)
        }));
      } catch (error) {
        console.error('Error fetching issuer names:', error);
      }
    };

    fetchIssuerNames();
  }, [displayProfile?.certificates, displayProfile?.my_badges]);

  // # Format display name for UI
  const formatDisplayName = (name) => {
    return name?.replace(/_/g, ' ') || name;
  };

  // # Effect to fetch and subscribe to profile data
  useEffect(() => {
    let unsubscribe;

    const fetchProfileData = async () => {
      try {
        if (viewUsername) {
          // # If viewing another user's profile, query by username
          const usersCollection = collection(db, 'users');
          const q = query(usersCollection, where('username', '==', viewUsername));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const userData = querySnapshot.docs[0].data();
            setDisplayProfile(userData);
          } else {
            setError('Profile not found');
          }
          setLoading(false);
        } else if (user?.uid) {
          // # If user viewing own profile (including admin)
          const profileRef = doc(db, 'users', user.uid);
          unsubscribe = onSnapshot(profileRef, (doc) => {
            if (doc.exists()) {
              setDisplayProfile(doc.data());
            } else {
              setError('Profile not found');
            }
            setLoading(false);
          }, (error) => {
            console.error('Error fetching profile:', error);
            setError('Error loading profile data');
            setLoading(false);
          });
        } else {
          setError('Please sign in to view profile');
          setLoading(false);
        }
      } catch (error) {
        console.error('Error setting up profile listener:', error);
        setError('Error loading profile data');
        setLoading(false);
      }
    };

    fetchProfileData();

    // # Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [viewUsername, user]);

  // # Show loading state
  if (loading) {
    return <div className="prfl_hnr_loading">Loading honors...</div>;
  }

  // # Show error state
  if (error) {
    return <div className="prfl_hnr_error">{error}</div>;
  }

  // # Check if user has access to view the profile
  const canViewProfile = viewUsername || user?.uid;
  if (!canViewProfile) {
    return <div className="prfl_hnr_error">Please sign in to view profile</div>;
  }

  return (
    <div className={`prfl_hnr_tab-content ${isDarkMode ? 'prfl_dark-mode' : ''}`}>
      {/* Certificates Section */}
      <div className="prfl_hnr_info-section">
        <h3><MdStars /> Certificates</h3>
        <div className="prfl_hnr_honors-grid">
          {displayProfile?.certificates?.length > 0 ? (
            displayProfile.certificates.map((cert) => (
              <div key={cert.id} className="prfl_hnr_honor-item prfl_hnr_certificate-item">
                <div className="prfl_hnr_honor-icon">
                  <i className="fas fa-certificate"></i>
                </div>
                <div className="prfl_hnr_honor-info">
                  <h4>{formatDisplayName(cert.name)}</h4>
                  <p>{cert.description}</p>
                  <p className="prfl_hnr_cert-details">
                    {/* # Display certificate details with proper formatting */}
                    Awarded on {new Date(cert.issueDate).toLocaleDateString()}
                    {cert.issuedBy && (
                      <span className="prfl_hnr_issued-by">
                        by {issuerNames[cert.issuedBy.uid] || 'Unknown User'}
                      </span>
                    )}
                  </p>
                  {cert.type && <span className="prfl_hnr_badge-type">{formatDisplayName(cert.type)}</span>}
                </div>
                {/* # Show download button only for own certificates */}
                {!viewUsername && user?.uid === displayProfile?.uid && (
                  <button 
                    className="prfl_hnr_download-btn"
                    onClick={() => generateCertificatePDF(displayProfile, cert)}
                    title="Download Certificate"
                  >
                    <i className="fas fa-download"></i>
                    Download
                  </button>
                )}
              </div>
            ))
          ) : (
            <p className="prfl_hnr_no-items">No certificates earned yet</p>
          )}
        </div>
      </div>

      {/* Badges Section */}
      <div className="prfl_hnr_info-section">
        <h3><MdStars /> Badges</h3>
        <div className="prfl_hnr_honors-grid">
          {displayProfile?.my_badges?.length > 0 ? (
            displayProfile.my_badges.map((badge, index) => (
              <div key={`${badge.id}-${index}`} className="prfl_hnr_honor-item prfl_hnr_badge-item">
                <div className="prfl_hnr_honor-icon" style={{ background: badge.color ? `${badge.color}22` : '#e7f3ff' }}>
                  {/* # Display badge icon or fallback icon */}
                  {badge.icon ? (
                    <img src={badge.icon} alt={badge.name} />
                  ) : (
                    <i className="fas fa-award"></i>
                  )}
                </div>
                <div className="prfl_hnr_honor-info">
                  <h4>{formatDisplayName(badge.name)}</h4>
                  <p>{badge.description}</p>
                  {/* # Display badge assigned details */}
                  {badge.assignedBy && (
                    <p className="prfl_hnr_badge-details">
                      Awarded on {new Date(badge.assignedAt).toLocaleDateString()}
                      <span className="prfl_hnr_assigned-by">
                        by {issuerNames[badge.assignedBy.uid] || 'Unknown User'}
                      </span>
                    </p>
                  )}
                  {badge.type && <span className="prfl_hnr_badge-type" style={{ background: badge.color ? `${badge.color}22` : '#e7f3ff', color: badge.color || '#0a66c2' }}>{formatDisplayName(badge.type)}</span>}
                </div>
              </div>
            ))
          ) : (
            <p className="prfl_hnr_no-items">No badges earned yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileHonors;
